import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import type { PhonePresence } from "@sukha/shared";
import { QUERY_KEYS } from "../queryKeys";
import { notifyWakeSignals, registerWakeRecoveryJob, unregisterWakeRecoveryJob } from "../wakeRecoveryGate";
import { useActiveSession } from "./useTimerSession";
import { useDistractions } from "./useDistractions";
import type { UseDistractionsOptions } from "./useDistractions";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConnectionStatus = "connected" | "distracted" | "disconnected";
export interface ConnectionStatusData {
  phoneStatus: ConnectionStatus;
  chromeStatus: ConnectionStatus;
  phoneIsEnabled: boolean;
  websiteIsEnabled: boolean;
  shouldShowOverlay: boolean;
  phone: {
    status: string;
    createdAt?: string | Date | null;
  } | null;
  desktop: {
    status: string;
    createdAt?: string | Date | null;
  } | null;
  isPhoneHeartbeatFresh: boolean;
  isDesktopHeartbeatFresh: boolean;
  /** True once a phone distraction row was seen this session and heartbeat is within timeout. */
  isPhoneOnline: boolean;
  /** True once a desktop distraction row was seen this session and heartbeat is within timeout. */
  isDesktopOnline: boolean;
  /** Sticky: once a non-null phone row was observed for the current session key. */
  hasEverSeenPhoneThisSession: boolean;
  /** Sticky: once a non-null desktop row was observed for the current session key. */
  hasEverSeenDesktopThisSession: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Expected period of a single device heartbeat, in milliseconds.
 *
 * The Chrome extension and desktop app both send a `session-active` heartbeat
 * every 60 seconds via `chrome.alarms`/native timer respectively, so a missed
 * tick should be treated as a transient hiccup rather than disconnection.
 */
const HEARTBEAT_INTERVAL_MS = 60_000;
/**
 * How long we treat a device as "fresh" since its last heartbeat.
 *
 * Set to 5x the heartbeat interval so the indicator stays stable through
 * short network blips, MV3 service-worker sleep gaps, and background-tab
 * polling delays. Must remain comfortably larger than the longest plausible
 * gap between heartbeats (alarm period + polling delay + jitter).
 */
const HEARTBEAT_TIMEOUT_MS = 5 * HEARTBEAT_INTERVAL_MS; // 5 minutes
const FRESHNESS_TICK_MS = 30_000; // Re-evaluate every 30s
/**
 * After a wake signal (focus/visibility/pageshow/online), how long a device
 * that looked fresh at the last evaluation keeps its freshness while the
 * wake-recovery resync fetches an up-to-date row. A frozen or heavily
 * throttled tab runs neither the freshness tick nor the distraction poll;
 * bumping the clock on wake without this grace flashed the badge "Off" until
 * the resync landed even though the device kept reporting the whole time.
 */
const WAKE_RESYNC_GRACE_MS = 15_000;

// ---------------------------------------------------------------------------
// Heartbeat wall-clock store (avoids React setState every 30s → all consumers)
// ---------------------------------------------------------------------------

let heartbeatNowMs = Date.now();
const heartbeatStoreListeners = new Set<() => void>();
function subscribeHeartbeatStore(onStoreChange: () => void): () => void {
  heartbeatStoreListeners.add(onStoreChange);
  return () => {
    heartbeatStoreListeners.delete(onStoreChange);
  };
}
function getHeartbeatNowSnapshot(): number {
  return heartbeatNowMs;
}
function bumpHeartbeatNow(): void {
  heartbeatNowMs = Date.now();
  for (const listener of heartbeatStoreListeners) {
    listener();
  }
}
let heartbeatIntervalStarted = false;
function ensureHeartbeatInterval(): void {
  if (heartbeatIntervalStarted || typeof window === "undefined") return;
  heartbeatIntervalStarted = true;
  setInterval(() => {
    bumpHeartbeatNow();
  }, FRESHNESS_TICK_MS);
}

// ---------------------------------------------------------------------------
// Desktop (Chrome extension) direct-liveness store
// ---------------------------------------------------------------------------

/**
 * Last wall-clock time the host app confirmed the Chrome extension is alive via
 * a direct, transport-independent channel (e.g. an `externally_connectable`
 * ping that wakes the MV3 service worker). This is a *second* freshness source
 * that does not depend on the distraction poll round-trip, so a backgrounded /
 * throttled web tab or a couple of slipped heartbeats can no longer wrongly
 * flip the desktop badge "Off" while the extension is genuinely running.
 */
let desktopLivenessAtMs = 0;
/**
 * Last wall-clock time the extension explicitly confirmed it is monitoring the
 * active session (newer builds answer `detectInstalled` with session context).
 * Unlike the plain liveness floor, this is strong enough to show "connected"
 * before the first same-session heartbeat row lands — it closes the
 * start-of-session gap where the badge wrongly said "connect the extension".
 */
let desktopMonitoringConfirmedAtMs = 0;
const desktopLivenessListeners = new Set<() => void>();
function subscribeDesktopLiveness(onStoreChange: () => void): () => void {
  desktopLivenessListeners.add(onStoreChange);
  return () => {
    desktopLivenessListeners.delete(onStoreChange);
  };
}
function getDesktopLivenessSnapshot(): number {
  return desktopLivenessAtMs;
}
function getDesktopMonitoringConfirmedSnapshot(): number {
  return desktopMonitoringConfirmedAtMs;
}

/**
 * Report that the Chrome extension was just confirmed alive via a direct ping.
 *
 * Call this from the host app whenever the extension answers an
 * `externally_connectable` message. Plain liveness only ever advances the
 * freshness floor (monotonic) and is folded into desktop freshness **only
 * after** a real heartbeat has been seen this session — it prevents a false
 * "Off" without manufacturing a false "On". Pass `monitoringConfirmed: true`
 * only when the extension's response explicitly says it is monitoring the
 * active session; that stronger signal may show "connected" before the first
 * same-session heartbeat row arrives.
 *
 * @param atMs - Confirmation time in ms since epoch. Defaults to `Date.now()`.
 * @param options - Set `monitoringConfirmed` when the response included
 *   session context proving the extension is monitoring the active session.
 */
export function reportDesktopExtensionAlive(atMs: number = Date.now(), options?: {
  monitoringConfirmed?: boolean;
}): void {
  if (!Number.isFinite(atMs)) return;
  let advanced = false;
  if (atMs > desktopLivenessAtMs) {
    desktopLivenessAtMs = atMs;
    advanced = true;
  }
  if (options?.monitoringConfirmed && atMs > desktopMonitoringConfirmedAtMs) {
    desktopMonitoringConfirmedAtMs = atMs;
    advanced = true;
  }
  if (!advanced) return;
  for (const listener of desktopLivenessListeners) {
    listener();
  }
}
/** Poll distractions quickly after session start so extension heartbeats show up without a ~60s wait. */
const DISTRACTION_BURST_AFTER_START_MS = 180_000;
const DISTRACTION_BURST_POLL_MS = 5_000;
const DISTRACTION_STEADY_POLL_MS = 60_000;
const DISTRACTION_POLL_JITTER_MS = 10_000;
/**
 * When the tab is in the background we slow polling instead of stopping it. If we return false
 * (no refetch) while `document.hidden` is true, the last `createdAt` in cache goes stale. When
 * the user comes back, `heartbeatNow` jumps forward and we falsely show "extension not connected"
 * until the next fetch — wake recovery resyncs the distraction query instead of TanStack
 * `refetchOnWindowFocus`.
 */
const DISTRACTION_BACKGROUND_POLL_MS = 90_000;
/** Poll quickly while the phone row is `distracted` so the badge clears soon after return. */
const DISTRACTION_PHONE_DISTRACTED_POLL_MS = 10_000;
// ---------------------------------------------------------------------------
// Module-level per-session seen state (survives component remount / gcTime)
// ---------------------------------------------------------------------------

export type DistractionRowStatus = "inactive" | "active" | "distracted";
type SeenState = {
  phoneSeenAt: number;
  desktopSeenAt: number;
  /** Newest raw row timestamp observed per device (server clock). */
  phoneRowTs: number;
  desktopRowTs: number;
  /** Wake-resync grace deadlines (see beginWakeFreshnessGrace). */
  phoneWakeGraceUntil: number;
  desktopWakeGraceUntil: number;
  everSeenPhone: boolean;
  everSeenDesktop: boolean;
  /** Last phone row status observed for this session (survives brief cache gaps). */
  lastKnownPhoneStatus: DistractionRowStatus | null;
  /** Last desktop row status observed for this session (survives brief cache gaps). */
  lastKnownDesktopStatus: DistractionRowStatus | null;
};

/**
 * Whether a device heartbeat floor is fresh at `nowMs`.
 *
 * @param lastMs - Monotonic freshness floor in ms since epoch (0 = never seen).
 * @param nowMs - Evaluation wall-clock in ms since epoch.
 * @param timeoutMs - Freshness window.
 * @param wakeGraceUntilMs - Optional wake-resync grace deadline; a stale floor
 *   still counts as fresh until this deadline (see WAKE_RESYNC_GRACE_MS).
 * @returns True when the floor is inside the window or covered by wake grace.
 */
export function isHeartbeatFresh(lastMs: number, nowMs: number, timeoutMs: number, wakeGraceUntilMs = 0): boolean {
  if (!lastMs) return false;
  if (nowMs - lastMs <= timeoutMs) return true;
  return nowMs <= wakeGraceUntilMs;
}

/**
 * Folds a newly observed distraction-row timestamp into the per-device
 * freshness floor.
 *
 * Rows are stamped with the *server* clock while freshness is judged against
 * the *client* clock, so trusting the raw row timestamp let server/client
 * clock skew silently burn the freshness budget and drop the badge during
 * ordinary polling gaps. When the row timestamp advances we know a heartbeat
 * just happened, so the floor is stamped with the client clock instead. The
 * first observation still trusts the row's own timestamp (capped at `nowMs`)
 * so a genuinely old row cannot fake freshness.
 *
 * @param params.prevRowTs - Newest row timestamp seen so far (0 = none).
 * @param params.prevSeenAt - Current freshness floor.
 * @param params.rowTs - Row timestamp observed now (server clock).
 * @param params.nowMs - Client wall-clock at observation.
 * @returns The next `{ rowTs, seenAt }` pair (unchanged when nothing advanced).
 */
export function advanceDeviceFreshnessFloor(params: {
  prevRowTs: number;
  prevSeenAt: number;
  rowTs: number;
  nowMs: number;
}): {
  rowTs: number;
  seenAt: number;
} {
  const {
    prevRowTs,
    prevSeenAt,
    rowTs,
    nowMs
  } = params;
  if (rowTs <= prevRowTs) return {
    rowTs: prevRowTs,
    seenAt: prevSeenAt
  };
  const observedAt = prevRowTs === 0 ? Math.min(rowTs, nowMs) : nowMs;
  return {
    rowTs,
    seenAt: Math.max(prevSeenAt, observedAt)
  };
}

/** Maps a distraction row status to the connection badge status. */
export function mapRowStatusToConnection(st: string | null | undefined): ConnectionStatus {
  if (st === "active") return "connected";
  if (st === "distracted") return "distracted";
  return "disconnected";
}

/**
 * Resolves phone connection status, falling back to the last known row status
 * when the TanStack cache row is briefly missing (break resume, refetch gap).
 */
export function resolvePhoneConnectionStatus(params: {
  isSessionActive: boolean;
  isOnBreak: boolean;
  everSeenPhone: boolean;
  isPhoneHeartbeatFresh: boolean;
  phone: {
    status: string;
  } | null;
  lastKnownPhoneStatus: DistractionRowStatus | null;
  /**
   * What asking the phone directly turned up, when anything was asked.
   *
   * Heartbeat freshness cannot notice a browser being killed: the last post is
   * good for five minutes, so the badge keeps whatever it last said. Asking can
   * — a phone that is gone does not answer.
   *
   * Only ever TIGHTENS. Silence is not proof of absence in general, so
   * `"absent"` is acted on and `"unknown"` changes nothing; the caller decides
   * when asking is meaningful at all, and does not ask when its own transport
   * is down.
   */
  phonePresence?: PhonePresence;
}): ConnectionStatus {
  if (!params.isSessionActive || params.isOnBreak) return "disconnected";
  if (!params.everSeenPhone) return "disconnected";
  if (params.phonePresence === "absent") return "disconnected";
  if (!params.isPhoneHeartbeatFresh) return "disconnected";
  const st = params.phone?.status ?? params.lastKnownPhoneStatus;
  if (!st) return "disconnected";
  return mapRowStatusToConnection(st);
}

/**
 * Resolves desktop connection status, falling back to the last known row status
 * when the TanStack cache row is briefly missing.
 *
 * When the heartbeat path cannot prove a connection (no same-session row seen
 * yet, or freshness lapsed), a fresh direct monitoring confirmation from the
 * extension resolves as "connected" — the extension answering "I am monitoring
 * this session" is at least as strong as a heartbeat row, and it closes the
 * start-of-session window where no row can exist yet.
 */
export function resolveDesktopConnectionStatus(params: {
  isSessionActive: boolean;
  everSeenDesktop: boolean;
  isDesktopHeartbeatFresh: boolean;
  desktop: {
    status: string;
  } | null;
  lastKnownDesktopStatus: DistractionRowStatus | null;
  isMonitoringConfirmedFresh?: boolean;
}): ConnectionStatus {
  if (!params.isSessionActive) return "disconnected";
  const st = params.desktop?.status ?? params.lastKnownDesktopStatus;
  if (params.everSeenDesktop && params.isDesktopHeartbeatFresh && st) {
    return mapRowStatusToConnection(st);
  }
  if (params.isMonitoringConfirmedFresh) return "connected";
  return "disconnected";
}
const seenBySession = new Map<string, SeenState>();
let storeVersion = 0;
const storeListeners = new Set<() => void>();
function subscribeConnectionStatusStore(cb: () => void): () => void {
  storeListeners.add(cb);
  return () => {
    storeListeners.delete(cb);
  };
}
function getConnectionStatusStoreVersion(): number {
  return storeVersion;
}
function notifyConnectionStatusStore(): void {
  storeVersion += 1;
  for (const l of storeListeners) {
    l();
  }
}
function ensureSeen(sessionKey: string): SeenState {
  let s = seenBySession.get(sessionKey);
  if (!s) {
    s = {
      phoneSeenAt: 0,
      desktopSeenAt: 0,
      phoneRowTs: 0,
      desktopRowTs: 0,
      phoneWakeGraceUntil: 0,
      desktopWakeGraceUntil: 0,
      everSeenPhone: false,
      everSeenDesktop: false,
      lastKnownPhoneStatus: null,
      lastKnownDesktopStatus: null
    };
    seenBySession.set(sessionKey, s);
  }
  return s;
}

/** Last non-empty session id; kept across `activeSession` undefined (refetch). Cleared when query `data` is `null` (no session); map entries are not deleted there to avoid transient-null wipes. */
let lastDefinedSessionIdForStore: string | null = null;
let prevPruneSessionId: string | null = null;
function resolveStoreSessionKey(rawSessionId: string | undefined | null, activeSession: unknown): string | null {
  if (typeof rawSessionId === "string" && rawSessionId.length > 0) {
    if (prevPruneSessionId && prevPruneSessionId !== rawSessionId) {
      if (seenBySession.has(prevPruneSessionId)) {
        seenBySession.delete(prevPruneSessionId);
      }
      // A monitoring confirmation belongs to the session it was given for;
      // don't let it carry a "connected" badge into the next session. The
      // 30s freshness tick re-evaluates after this reset.
      desktopMonitoringConfirmedAtMs = 0;
    }
    prevPruneSessionId = rawSessionId;
    lastDefinedSessionIdForStore = rawSessionId;
    return rawSessionId;
  }
  // API "no session" (`data === null`). Do **not** delete `seenBySession` entries here:
  // the web app's `useActiveSession` query can briefly surface `null` during refetch/retry
  // races (`fetchActiveSessionWithRetry`) even while a session is still in progress.
  // Wiping the map reset `everSeen*` and the monotonic floor — the desktop pill flips
  // "Off" until the next distraction heartbeat. Toggling break happened to refetch and
  // recover, which matched that failure mode. Orphan map rows are cheap; we still prune
  // when the session **id** changes (see branch above).
  if (activeSession === null) {
    lastDefinedSessionIdForStore = null;
    prevPruneSessionId = null;
    return null;
  }
  return lastDefinedSessionIdForStore;
}

/**
 * Grants a short freshness grace to devices that looked fresh at the last
 * evaluation, right before a wake signal bumps the freshness clock.
 *
 * Must run *before* `bumpHeartbeatNow()` in wake handlers: the decision uses
 * the pre-wake clock, which is exactly how the UI last judged freshness.
 * Devices that were already stale at the last evaluation get no grace, so a
 * genuinely dead device stays "Off" through a wake.
 */
function beginWakeFreshnessGrace(): void {
  const key = lastDefinedSessionIdForStore;
  if (!key) return;
  const s = seenBySession.get(key);
  if (!s) return;
  const lastEvaluatedNow = heartbeatNowMs;
  const graceUntil = Date.now() + WAKE_RESYNC_GRACE_MS;
  let changed = false;
  if (isHeartbeatFresh(s.phoneSeenAt, lastEvaluatedNow, HEARTBEAT_TIMEOUT_MS) && graceUntil > s.phoneWakeGraceUntil) {
    s.phoneWakeGraceUntil = graceUntil;
    changed = true;
  }
  const desktopFloor = s.everSeenDesktop ? Math.max(s.desktopSeenAt, desktopLivenessAtMs) : s.desktopSeenAt;
  if (isHeartbeatFresh(desktopFloor, lastEvaluatedNow, HEARTBEAT_TIMEOUT_MS) && graceUntil > s.desktopWakeGraceUntil) {
    s.desktopWakeGraceUntil = graceUntil;
    changed = true;
  }
  if (changed) notifyConnectionStatusStore();
}

/** Last known phone status for a session (for poll tuning outside the hook). */
export function getLastKnownPhoneStatus(sessionId: string | undefined | null): DistractionRowStatus | null {
  if (typeof sessionId !== "string" || sessionId.length === 0) return null;
  return seenBySession.get(sessionId)?.lastKnownPhoneStatus ?? null;
}
function logConnectionStatusTransition(kind: "phoneStatus" | "chromeStatus", from: ConnectionStatus, to: ConnectionStatus, payload: Record<string, unknown>): void {
  if (from === to) return;
  try {
    console.info(`[ConnectionStatus] ${kind} transition`, JSON.stringify({
      from,
      to,
      ...payload
    }));
  } catch {
    console.info("[ConnectionStatus] transition", kind, from, "->", to);
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Derives phone and Chrome extension connection status from distraction data.
 *
 * Heartbeat freshness is re-evaluated every minute and immediately when the
 * tab regains focus (on platforms that support visibility/focus events).
 *
 * Prefer {@link useConnectionStatus} from `ConnectionStatusProvider` in UI code.
 * This value hook should run once inside that provider.
 *
 * @param distractionOptions - Optional overrides forwarded to `useDistractions`.
 */
export function useConnectionStatusValue(distractionOptions?: UseDistractionsOptions,
/**
 * What asking the phone directly turned up, supplied by the host app.
 *
 * The exchange itself lives in `apps/web` because it needs that app's
 * realtime transport; this package only needs the verdict. Omitted, nothing
 * changes and the badge is decided by heartbeat freshness exactly as before —
 * which is also what happens for any host that never asks.
 */
phonePresence?: PhonePresence): ConnectionStatusData {
  const queryClient = useQueryClient();
  const {
    data: activeSession
  } = useActiveSession();
  const isSessionActive = !!activeSession?.session?.id;
  const isOnBreak = Boolean(activeSession?.isPaused);
  const prevPausedRef = useRef(isOnBreak);
  const sessionIdForQuery = activeSession?.session?.id;
  const hasSessionIdForQuery = typeof sessionIdForQuery === "string" && sessionIdForQuery.length > 0;

  // Poll jitter desyncs clients but must be STABLE across evaluations:
  // TanStack clears and re-creates the interval timer whenever the callback
  // returns a different value (queryObserver setOptions), and this hook
  // re-renders every FRESHNESS_TICK_MS — per-evaluation jitter reset the
  // timer before any interval longer than the tick could fire, so the steady
  // poll never ran at all.
  const distractedPollMsRef = useRef(DISTRACTION_PHONE_DISTRACTED_POLL_MS + Math.random() * 800);
  const burstPollMsRef = useRef(DISTRACTION_BURST_POLL_MS + Math.random() * 800);
  const steadyPollMsRef = useRef(DISTRACTION_STEADY_POLL_MS + (Math.random() * 2 - 1) * DISTRACTION_POLL_JITTER_MS);
  const {
    data: distractions
  } = useDistractions({
    refetchInterval: () => {
      if (typeof document !== "undefined" && document.hidden) {
        return DISTRACTION_BACKGROUND_POLL_MS;
      }
      // Never return false here: the ["activeSession"] cache can transiently
      // blip null (refetch/retry races — see resolveStoreSessionKey), and
      // TanStack does not re-evaluate a stopped interval, so returning false
      // once killed distraction polling permanently and the phone/desktop
      // badges stayed "Off". The `enabled` gate below already prevents
      // fetches when the session is truly gone.
      if (!activeSession?.session?.id) {
        return DISTRACTION_STEADY_POLL_MS;
      }
      const cachedDistractions = queryClient.getQueryData<{
        phone?: {
          status?: string;
        } | null;
      }>(QUERY_KEYS.distractions(activeSession.session.id));
      const lastKnownPhoneStatus = getLastKnownPhoneStatus(activeSession.session.id);
      if (cachedDistractions?.phone?.status === "distracted" || lastKnownPhoneStatus === "distracted") {
        return distractedPollMsRef.current;
      }
      const startedAt = activeSession.session.startedAt;
      if (startedAt) {
        const age = Date.now() - Date.parse(startedAt);
        if (age >= 0 && age < DISTRACTION_BURST_AFTER_START_MS) {
          return burstPollMsRef.current;
        }
      }
      return steadyPollMsRef.current;
    },
    // Keep polling while the tab is hidden/blurred. The distraction freshness
    // floor below only advances from this poll or from realtime messages; since
    // client-side distraction RT publishes were removed, a paused background
    // poll would let the floor age past HEARTBEAT_TIMEOUT_MS and flip the
    // phone/desktop badges to "Off" until the tab regained focus.
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 30_000,
    gcTime: 60_000,
    enabled: isSessionActive,
    ...distractionOptions
  });

  /** Busts in-memory cache for `GET /sessions/distraction` so a real network read runs. */
  const resyncDistractionsQuery = useCallback(() => {
    if (!hasSessionIdForQuery) return;
    void queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.distractions(sessionIdForQuery),
      exact: true
    });
  }, [hasSessionIdForQuery, queryClient, sessionIdForQuery]);
  ensureHeartbeatInterval();
  const heartbeatNow = useSyncExternalStore(subscribeHeartbeatStore, getHeartbeatNowSnapshot, getHeartbeatNowSnapshot);

  // Break enter/exit changes server + realtime distraction semantics; force a distraction
  // refetch so `createdAt` and status stay aligned (user-reported: toggling break fixed a
  // stuck "Off" pill without any extension change).
  useEffect(() => {
    if (!isSessionActive) {
      prevPausedRef.current = isOnBreak;
      return;
    }
    if (prevPausedRef.current === isOnBreak) return;
    prevPausedRef.current = isOnBreak;
    resyncDistractionsQuery();
  }, [isOnBreak, isSessionActive, resyncDistractionsQuery]);

  // A session-id (re)appearing — session start, or recovery from a transient
  // activeSession cache blip — forces a fetch, which also restarts the
  // refetchInterval chain if a blip ever stalled it.
  const prevSessionIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!hasSessionIdForQuery) return;
    if (prevSessionIdRef.current === sessionIdForQuery) return;
    prevSessionIdRef.current = sessionIdForQuery;
    resyncDistractionsQuery();
  }, [hasSessionIdForQuery, sessionIdForQuery, resyncDistractionsQuery]);
  const distractionWakeSessionRef = useRef(false);
  const resyncDistractionsRef = useRef(resyncDistractionsQuery);
  distractionWakeSessionRef.current = isSessionActive && hasSessionIdForQuery;
  resyncDistractionsRef.current = resyncDistractionsQuery;
  const DISTRACTION_WAKE_JOB_KEY = "connection-status-distraction";
  useEffect(() => {
    registerWakeRecoveryJob(DISTRACTION_WAKE_JOB_KEY, () => {
      if (distractionWakeSessionRef.current) {
        resyncDistractionsRef.current();
      }
    });
    return () => {
      unregisterWakeRecoveryJob(DISTRACTION_WAKE_JOB_KEY);
    };
  }, []);

  // bfcache restore / network return: coalesce resync via wake gate
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPageShow = () => {
      beginWakeFreshnessGrace();
      bumpHeartbeatNow();
      notifyWakeSignals("pageshow");
    };
    const onOnline = () => {
      beginWakeFreshnessGrace();
      bumpHeartbeatNow();
      notifyWakeSignals("online");
    };
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  // Focus / visibility: bump clock immediately; resync runs once via wake gate debounce
  useEffect(() => {
    const onWindowFocus = () => {
      beginWakeFreshnessGrace();
      bumpHeartbeatNow();
      if (isSessionActive) {
        notifyWakeSignals("focus");
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        beginWakeFreshnessGrace();
      }
      bumpHeartbeatNow();
      if (document.visibilityState === "visible" && isSessionActive) {
        notifyWakeSignals("visibility_visible");
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("focus", onWindowFocus);
      document.addEventListener("visibilitychange", onVisibility);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", onWindowFocus);
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [isSessionActive]);
  const getLastHeartbeatMs = useCallback((row: {
    createdAt?: string | Date | null;
  } | null) => {
    const ts = row?.createdAt;
    if (!ts) return 0;
    const t = typeof ts === "string" ? Date.parse(ts) : new Date(ts).getTime();
    return Number.isFinite(t) ? t : 0;
  }, []);
  const phone = distractions?.phone ?? null;
  /**
   * The browser's row — what this hook has always called "desktop".
   *
   * It is the Chrome extension: everything below about desktop liveness is
   * about the extension's heartbeat.
   *
   * The fallback is load-bearing RIGHT NOW and must not be removed: production
   * still runs the pre-split API, whose response is `{ phone, desktop }` with no
   * `website` key at all (`origin/main` sessions.ts:758). Reading `website`
   * first and falling back is what makes this work against both.
   *
   * **When the split API deploys, this fallback has to go** — earlier than the
   * note here used to say. The server now buckets on the url's `app:` prefix
   * rather than on `type`, so `desktop` means a genuine desktop-app
   * distraction, not a legacy browser row. Once `website` is populated for
   * browser rows, a surviving `??` would light the extension's indicator for an
   * app distraction whenever there is no browser one — the exact mis-read the
   * server-side change removed.
   */
  const desktop = distractions?.website ?? distractions?.desktop ?? null;
  const rawSessionId = activeSession?.session?.id;
  const storeSessionKey = resolveStoreSessionKey(rawSessionId, activeSession);
  const connectionStatusStoreVersion = useSyncExternalStore(subscribeConnectionStatusStore, getConnectionStatusStoreVersion, getConnectionStatusStoreVersion);

  // Advance monotonic floors + sticky ever-seen into module store
  useEffect(() => {
    if (!storeSessionKey) return;
    const s = ensureSeen(storeSessionKey);
    const nowMs = Date.now();
    let changed = false;
    const pTs = getLastHeartbeatMs(phone);
    if (phone) {
      if (!s.everSeenPhone) {
        s.everSeenPhone = true;
        changed = true;
      }
      const phoneSt = phone.status as DistractionRowStatus;
      if ((phoneSt === "active" || phoneSt === "distracted" || phoneSt === "inactive") && s.lastKnownPhoneStatus !== phoneSt) {
        s.lastKnownPhoneStatus = phoneSt;
        changed = true;
      }
    }
    const pAdv = advanceDeviceFreshnessFloor({
      prevRowTs: s.phoneRowTs,
      prevSeenAt: s.phoneSeenAt,
      rowTs: pTs,
      nowMs
    });
    if (pAdv.rowTs !== s.phoneRowTs || pAdv.seenAt !== s.phoneSeenAt) {
      s.phoneRowTs = pAdv.rowTs;
      s.phoneSeenAt = pAdv.seenAt;
      changed = true;
    }
    const dTs = getLastHeartbeatMs(desktop);
    if (desktop) {
      if (!s.everSeenDesktop) {
        s.everSeenDesktop = true;
        changed = true;
      }
      const desktopSt = desktop.status as DistractionRowStatus;
      if ((desktopSt === "active" || desktopSt === "distracted" || desktopSt === "inactive") && s.lastKnownDesktopStatus !== desktopSt) {
        s.lastKnownDesktopStatus = desktopSt;
        changed = true;
      }
    }
    const dAdv = advanceDeviceFreshnessFloor({
      prevRowTs: s.desktopRowTs,
      prevSeenAt: s.desktopSeenAt,
      rowTs: dTs,
      nowMs
    });
    if (dAdv.rowTs !== s.desktopRowTs || dAdv.seenAt !== s.desktopSeenAt) {
      s.desktopRowTs = dAdv.rowTs;
      s.desktopSeenAt = dAdv.seenAt;
      changed = true;
    }
    if (changed) notifyConnectionStatusStore();
  }, [storeSessionKey, phone, desktop, getLastHeartbeatMs]);
  const seen = useMemo(() => {
    void connectionStatusStoreVersion;
    return storeSessionKey ? ensureSeen(storeSessionKey) : null;
  }, [storeSessionKey, connectionStatusStoreVersion]);
  const phoneSeenAt = seen?.phoneSeenAt ?? 0;
  const desktopSeenAt = seen?.desktopSeenAt ?? 0;
  const phoneWakeGraceUntil = seen?.phoneWakeGraceUntil ?? 0;
  const desktopWakeGraceUntil = seen?.desktopWakeGraceUntil ?? 0;
  const everSeenPhone = seen?.everSeenPhone ?? false;
  const everSeenDesktop = seen?.everSeenDesktop ?? false;
  const lastKnownPhoneStatus_0 = seen?.lastKnownPhoneStatus ?? null;
  const lastKnownDesktopStatus = seen?.lastKnownDesktopStatus ?? null;
  const desktopLivenessAt = useSyncExternalStore(subscribeDesktopLiveness, getDesktopLivenessSnapshot, getDesktopLivenessSnapshot);
  const desktopMonitoringConfirmedAt = useSyncExternalStore(subscribeDesktopLiveness, getDesktopMonitoringConfirmedSnapshot, getDesktopMonitoringConfirmedSnapshot);
  const isPhoneHeartbeatFresh = useMemo(() => isHeartbeatFresh(phoneSeenAt, heartbeatNow, HEARTBEAT_TIMEOUT_MS, phoneWakeGraceUntil), [phoneSeenAt, heartbeatNow, phoneWakeGraceUntil]);
  // Fold the direct extension-liveness floor into desktop freshness, but only
  // once a real heartbeat has been seen this session. That keeps "never wrongly
  // show Off" (a live extension extends an established connection) without ever
  // manufacturing a false "On" for an extension that is installed but not
  // actually monitoring this session.
  const desktopFreshnessFloor = everSeenDesktop ? Math.max(desktopSeenAt, desktopLivenessAt) : desktopSeenAt;
  const isDesktopHeartbeatFresh = useMemo(() => isHeartbeatFresh(desktopFreshnessFloor, heartbeatNow, HEARTBEAT_TIMEOUT_MS, desktopWakeGraceUntil), [desktopFreshnessFloor, heartbeatNow, desktopWakeGraceUntil]);
  const isDesktopMonitoringConfirmedFresh = useMemo(() => isHeartbeatFresh(desktopMonitoringConfirmedAt, heartbeatNow, HEARTBEAT_TIMEOUT_MS), [desktopMonitoringConfirmedAt, heartbeatNow]);
  const isPhoneOnline = everSeenPhone && isPhoneHeartbeatFresh;
  const isDesktopOnline = everSeenDesktop && isDesktopHeartbeatFresh || isSessionActive && isDesktopMonitoringConfirmedFresh;
  const phoneStatus = useMemo<ConnectionStatus>(() => resolvePhoneConnectionStatus({
    isSessionActive,
    isOnBreak,
    everSeenPhone,
    isPhoneHeartbeatFresh,
    phone,
    lastKnownPhoneStatus: lastKnownPhoneStatus_0,
    phonePresence
  }), [phonePresence, isSessionActive, isOnBreak, everSeenPhone, isPhoneHeartbeatFresh, phone, lastKnownPhoneStatus_0]);
  const chromeStatus = useMemo<ConnectionStatus>(() => resolveDesktopConnectionStatus({
    isSessionActive,
    everSeenDesktop,
    isDesktopHeartbeatFresh,
    desktop,
    lastKnownDesktopStatus,
    isMonitoringConfirmedFresh: isDesktopMonitoringConfirmedFresh
  }), [isSessionActive, everSeenDesktop, isDesktopHeartbeatFresh, desktop, lastKnownDesktopStatus, isDesktopMonitoringConfirmedFresh]);
  const prevPhoneStatusRef = useRef<ConnectionStatus | null>(null);
  const prevChromeStatusRef = useRef<ConnectionStatus | null>(null);
  useEffect(() => {
    const from = prevPhoneStatusRef.current;
    const to = phoneStatus;
    if (from === null) {
      prevPhoneStatusRef.current = to;
      return;
    }
    if (from !== to) {
      logConnectionStatusTransition("phoneStatus", from, to, {
        sessionId: storeSessionKey,
        hasPhoneRow: Boolean(phone),
        phoneStatus: phone?.status ?? null,
        phoneRowCreatedAt: phone?.createdAt instanceof Date ? phone.createdAt.toISOString() : phone?.createdAt ?? null,
        phoneSeenAt,
        ageSinceFloorSec: phoneSeenAt > 0 ? Math.round((heartbeatNow - phoneSeenAt) / 1000) : null,
        isPhoneHeartbeatFresh,
        hasEverSeenPhoneThisSession: everSeenPhone,
        documentHidden: typeof document !== "undefined" ? document.hidden : null,
        online: typeof navigator !== "undefined" ? navigator.onLine : null
      });
      prevPhoneStatusRef.current = to;
    }
  }, [phoneStatus, phone, phoneSeenAt, heartbeatNow, isPhoneHeartbeatFresh, everSeenPhone, storeSessionKey]);
  useEffect(() => {
    const from_0 = prevChromeStatusRef.current;
    const to_0 = chromeStatus;
    if (from_0 === null) {
      prevChromeStatusRef.current = to_0;
      return;
    }
    if (from_0 !== to_0) {
      logConnectionStatusTransition("chromeStatus", from_0, to_0, {
        sessionId: storeSessionKey,
        hasDesktopRow: Boolean(desktop),
        desktopStatus: desktop?.status ?? null,
        desktopRowCreatedAt: desktop?.createdAt instanceof Date ? desktop.createdAt.toISOString() : desktop?.createdAt ?? null,
        desktopSeenAt,
        ageSinceFloorSec: desktopSeenAt > 0 ? Math.round((heartbeatNow - desktopSeenAt) / 1000) : null,
        isDesktopHeartbeatFresh,
        hasEverSeenDesktopThisSession: everSeenDesktop,
        isOnBreak,
        documentHidden: typeof document !== "undefined" ? document.hidden : null,
        online: typeof navigator !== "undefined" ? navigator.onLine : null
      });
      prevChromeStatusRef.current = to_0;
    }
  }, [chromeStatus, desktop, desktopSeenAt, heartbeatNow, isDesktopHeartbeatFresh, everSeenDesktop, isOnBreak, storeSessionKey]);
  const phoneIsEnabled = phoneStatus === "connected" || phoneStatus === "distracted";
  const websiteIsEnabled = chromeStatus === "connected" || chromeStatus === "distracted";
  const shouldShowOverlay = isSessionActive && !(phoneIsEnabled && websiteIsEnabled);
  return useMemo((): ConnectionStatusData => ({
    phoneStatus,
    chromeStatus,
    phoneIsEnabled,
    websiteIsEnabled,
    shouldShowOverlay,
    phone,
    desktop,
    isPhoneHeartbeatFresh,
    isDesktopHeartbeatFresh,
    isPhoneOnline,
    isDesktopOnline,
    hasEverSeenPhoneThisSession: everSeenPhone,
    hasEverSeenDesktopThisSession: everSeenDesktop
  }), [phoneStatus, chromeStatus, phoneIsEnabled, websiteIsEnabled, shouldShowOverlay, phone, desktop, isPhoneHeartbeatFresh, isDesktopHeartbeatFresh, isPhoneOnline, isDesktopOnline, everSeenPhone, everSeenDesktop]);
}